import React, { useState, useEffect, useMemo } from 'react';
import './BookTime.css';
import { FaClock, FaCheckCircle, FaSpinner } from 'react-icons/fa';
import { useSearchParams } from 'react-router-dom';

const CALENDAR_API_URL = 'https://script.googleusercontent.com/macros/echo?user_content_key=vZ-y2tj9cqpRucX0Z0EQCR2QDhiBytjAXJhqwgLrO0U68yZyTeMvASUm7uToSUo5OR07rvityMppvq5JSkRj8wx06AV9OlsIm5_BxDlH2jW0nuo2oDemN9CCS2h10ox_1xSncGQajx_ryfhECjZEnN-m9i9IrPvUvHcQEm5QVkspIbs4W1RlL6BVtYbiWQL2XSC7W709wbCJFFZWN-UJtw91CeNAzs__MlZcHFAoI340AC4A1dKiFQ&lib=MNAePv3as6RmFFvUuXPX_fQ5P-Idvl_o1';

const TUTORING_LABELS = {
    math: 'Mathematics',
    coding: 'Coding',
    cs: 'Computer Science'
};

const TUTORING_PRICES = {
    math: 32,
    coding: 32,
    cs: 32
};

const BookTime = () => {
    const [searchParams] = useSearchParams();
    // const navigate = useNavigate(); // Unused
    const [busySlots, setBusySlots] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedSlots, setSelectedSlots] = useState([]);
    // const [bookingConfirmed, setBookingConfirmed] = useState(false); // Unused

    const [processingPayment, setProcessingPayment] = useState(false);

    // const sessionId = searchParams.get('session_id'); // No longer needed for entry
    const tutoringType = searchParams.get('type') || 'math';

    // Generate available time slots for the next 14 days
    const availableSlots = useMemo(() => {
        const slots = [];
        const now = new Date();

        for (let day = 1; day <= 14; day++) {
            const date = new Date(now);
            date.setDate(date.getDate() + day);

            // Skip weekends for tutoring
            if (date.getDay() === 0) continue; // Sunday

            // Available hours: 9 AM to 8 PM CT
            for (let hour = 9; hour <= 20; hour++) {
                const slotStart = new Date(date);
                slotStart.setHours(hour, 0, 0, 0);

                const slotEnd = new Date(slotStart);
                slotEnd.setHours(hour + 1);

                slots.push({
                    id: `${date.toISOString().split('T')[0]}-${hour}`,
                    date: new Date(date),
                    startTime: new Date(slotStart),
                    endTime: new Date(slotEnd),
                    displayDate: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
                    displayTime: `${hour > 12 ? hour - 12 : hour}:00 ${hour >= 12 ? 'PM' : 'AM'} - ${(hour + 1) > 12 ? (hour + 1) - 12 : (hour + 1)}:00 ${(hour + 1) >= 12 ? 'PM' : 'AM'}`
                });
            }
        }
        return slots;
    }, []);

    // Fetch calendar busy times
    useEffect(() => {
        const fetchCalendar = async () => {
            try {
                const response = await fetch(CALENDAR_API_URL);
                const data = await response.json();

                // Extract busy time ranges from calendar events
                const busy = Object.values(data).map(event => ({
                    start: new Date(event.startTime),
                    end: new Date(event.endTime)
                }));

                setBusySlots(busy);
            } catch (error) {
                console.error('Error fetching calendar:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchCalendar();
    }, []);

    // Check if a slot conflicts with busy times
    const isSlotAvailable = (slot) => {
        return !busySlots.some(busy => {
            return (slot.startTime < busy.end && slot.endTime > busy.start);
        });
    };

    // Filter to only available slots
    const freeSlots = useMemo(() => {
        return availableSlots.filter(isSlotAvailable);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availableSlots, busySlots]);

    // Group slots by date
    const slotsByDate = useMemo(() => {
        const grouped = {};
        freeSlots.forEach(slot => {
            const dateKey = slot.displayDate;
            if (!grouped[dateKey]) {
                grouped[dateKey] = [];
            }
            grouped[dateKey].push(slot);
        });
        return grouped;
    }, [freeSlots]);

    const handleSelectSlot = (slot) => {
        setSelectedSlots(prev => {
            const isSelected = prev.some(s => s.id === slot.id);
            if (isSelected) {
                return prev.filter(s => s.id !== slot.id);
            } else {
                return [...prev, slot];
            }
        });
    };

    const handleProceedToPayment = async () => {
        if (selectedSlots.length === 0) return;

        setProcessingPayment(true);
        try {
            const bookingDetails = selectedSlots.map(slot => ({
                date: slot.displayDate,
                time: slot.displayTime,
                isoDate: slot.date,
                isoStartTime: slot.startTime,
                isoEndTime: slot.endTime
            }));

            const response = await fetch('/api/create-checkout', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    productType: tutoringType,
                    quantity: selectedSlots.length,
                    bookingDetails: bookingDetails
                })
            });
            const data = await response.json();
            if (data.url) {
                window.location.href = data.url;
            } else {
                alert('Error creating checkout session');
                setProcessingPayment(false);
            }
        } catch (error) {
            console.error('Checkout error:', error);
            alert('Error connecting to payment system');
            setProcessingPayment(false);
        }
    };



    return (
        <div className="book-time-container">
            <div className="book-time-header-gradient" />

            <div className="book-time-content">
                <section className="book-time-header">

                    <h1>Book Your Session</h1>
                    <p>Select time slots for your <strong>{TUTORING_LABELS[tutoringType]}</strong> tutoring session</p>
                    <p style={{ marginTop: '8px', fontSize: '14px', color: '#ffffffff', fontWeight: 600 }}>All times are in Central Time (CT)</p>
                </section>

                {loading ? (
                    <div className="loading-state">
                        <FaSpinner className="spinner" />
                        <p>Loading available times...</p>
                    </div>
                ) : (
                    <>
                        <div className="slots-container">
                            {Object.entries(slotsByDate).map(([date, slots]) => (
                                <div key={date} className="date-group">
                                    <h3 className="date-header">{date}</h3>
                                    <div className="time-slots">
                                        {slots.map(slot => {
                                            const isSelected = selectedSlots.some(s => s.id === slot.id);
                                            return (
                                                <button
                                                    key={slot.id}
                                                    className={`time-slot ${isSelected ? 'selected' : ''}`}
                                                    onClick={() => handleSelectSlot(slot)}
                                                >
                                                    <FaClock className="slot-icon" />
                                                    <span>{slot.displayTime}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {selectedSlots.length > 0 && (
                            <div className="booking-confirmation">
                                <div className="selected-info">
                                    <h3>{selectedSlots.length} Session{selectedSlots.length > 1 ? 's' : ''} Selected</h3>

                                    {/* Discount Logic: Buy 5 Get 1 Free (Every 6th is free) */}
                                    {(() => {
                                        const count = selectedSlots.length;
                                        const freeCount = Math.floor(count / 6);
                                        const basePrice = TUTORING_PRICES[tutoringType] || 32;
                                        const totalPrice = (count - freeCount) * basePrice;
                                        // const nextFree = 6 - (count % 6);

                                        return (
                                            <div className="price-breakdown">
                                                <p className="total-price">
                                                    Total: ${totalPrice}
                                                    {freeCount > 0 && <span className="original-price">${count * basePrice}</span>}
                                                </p>
                                                {freeCount > 0 && (
                                                    <p className="savings-badge">
                                                        <FaCheckCircle style={{ marginRight: '4px' }} />
                                                        {freeCount} Free Session{freeCount > 1 ? 's' : ''} Applied!
                                                    </p>
                                                )}
                                                {count % 6 !== 0 && (
                                                    <p className="discount-nudge">
                                                        Add {6 - (count % 6)} more for {freeCount > 0 ? 'another' : 'a'} free session!
                                                    </p>
                                                )}
                                            </div>
                                        );
                                    })()}
                                </div>
                                <button
                                    className="confirm-btn"
                                    onClick={handleProceedToPayment}
                                    disabled={processingPayment}
                                >
                                    {processingPayment ? 'Processing...' : 'Proceed to Payment'}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default BookTime;
